import { FC, useContext, useMemo } from 'react';

import { AlertIcon, H3, Toggle } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';

import { useIsFeatureAvailable } from 'features';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { EDuration, ETariffType } from 'types';

import { BOOKABLE_TARIFFS } from '../constants';
import s from '../Tariff.module.scss';
import { ITariffAdditionalSectionProps } from './interfaces';

export const TariffAdditionalSection: FC<ITariffAdditionalSectionProps> = ({
  control,
  tariffType,
  setValue,
  watch,
  duration,
  durationType,
  showInShell,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { telegramChannelId, holidayTariffsEnabled },
    },
  } = useContext(SettingsContext);

  const isCashBoxAvailable = useIsFeatureAvailable('cash_box');
  const isBookingAvailable = useIsFeatureAvailable('booking');

  const price = watch('price_list');

  const zeroPriceValue = price.some(
    (item) => item.cost === 0 || item.user_cost === 0
  );

  const isDisableBookable: boolean = useMemo(() => {
    if (!BOOKABLE_TARIFFS.includes(tariffType)) {
      return true;
    }

    if (tariffType === ETariffType.FIXED) {
      if (duration === 0) {
        setValue('online_booking_enabled', false);
        return true;
      }

      switch (durationType) {
        case String(EDuration.days):
          if (Number(duration) * 24 < 1 || duration > 1) {
            setValue('online_booking_enabled', false);
            return true;
          }
          break;
        case String(EDuration.hours):
          if (duration > 24 || duration < 1) {
            setValue('online_booking_enabled', false);
            return true;
          }
          break;
        case String(EDuration.weeks): {
          if (Number(duration) * 168 < 1 || Number(duration) * 168 > 24)
            setValue('online_booking_enabled', false);
          return true;
        }
        case String(EDuration.minutes):
          if (duration < 60 || duration > 1440) {
            setValue('online_booking_enabled', false);
            return true;
          }
          break;
        default:
          return false;
      }
    }

    return !isBookingAvailable;
  }, [tariffType, duration, durationType, isBookingAvailable]);

  return (
    <div className={s.additional_block}>
      <H3 className={s.header}>{t('additional')}</H3>
      <div className={cn(s.fields, s.additional)}>
        <Controller
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Toggle
              {...field}
              label={t('tariff.discount.apply')}
              iconName="PercentIcon"
            />
          )}
          name="use_global_discounts"
        />
        {tariffType !== 'PER_MINUTE' && (
          <Controller
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Toggle
                {...field}
                label={t('tariff.show.admin')}
                iconName="CircleRubleIcon"
              />
            )}
            name="show_in_billing"
          />
        )}
        <div className={s.with_info}>
          <Controller
            render={({ field }) => (
              <Toggle
                {...field}
                iconName="PrinterIcon"
                label={t('tariff.printing')}
                disabled={!isCashBoxAvailable || !zeroPriceValue}
              />
            )}
            name="disable_sale_receipt_printing"
            control={control}
          />
          {!zeroPriceValue && (
            <p className={s.printing_text}>{t('tariff.printing.info')}</p>
          )}
        </div>
        <Controller
          control={control}
          render={({ field: { ref, ...field } }) => (
            <Toggle
              {...field}
              label={t('tariff.show.client')}
              iconName="OpenedEyeIcon"
            />
          )}
          name="show_in_shell"
        />
        {showInShell && (
          <Controller
            control={control}
            render={({ field: { ref, ...field } }) => (
              <Toggle
                {...field}
                label={t('tariff.show.highlight')}
                iconName="StarsIcon"
              />
            )}
            name="highlighted"
          />
        )}
        <div className={s.with_info}>
          <Controller
            render={({ field }) => (
              <Toggle
                {...field}
                iconName="CalendarDayIcon"
                label={t('tariff.isHoliday')}
                className={s.isHoliday}
                disabled={!holidayTariffsEnabled}
              />
            )}
            name="use_on_holidays"
            control={control}
          />
          {!holidayTariffsEnabled && (
            <span className={s.info_text}>
              {t('settings.in')}{' '}
              <NavLink
                state={{ selectedTab: 'BILLING' }}
                to={'/settings'}
                className={s.info_link}
              >
                {t('settings.tabs.billing')}
              </NavLink>{' '}
              {t('tariffs.holidays.disabled.info_2')}
            </span>
          )}
        </div>
        <div className={s.with_info}>
          <Controller
            render={({ field }) => (
              <Toggle
                {...field}
                iconName="BellIcon"
                label={t('telegram.notify')}
                disabled={!telegramChannelId?.id}
              />
            )}
            name="is_tracked"
            control={control}
          />
          {!telegramChannelId?.id && (
            <span className={s.info_text}>
              {t('settings.in')}{' '}
              <NavLink
                state={{ selectedTab: 'INTEGRATIONS' }}
                to={'/settings'}
                className={s.info_link}
              >
                {t('settings.tab.integration')}
              </NavLink>{' '}
              (Telegram) {t('tariffs.telegram.disabled.info_2')}
            </span>
          )}
        </div>
        <Controller
          render={({ field }) => (
            <Toggle
              {...field}
              iconName="CheckedCalendarIcon"
              label={t('tariff.bookable')}
              disabled={isDisableBookable}
            />
          )}
          name="online_booking_enabled"
          control={control}
        />
        {isDisableBookable && isBookingAvailable && (
          <div className={s.helper}>
            <AlertIcon size="s" />
            {t('tariff.bookable.helper')}
          </div>
        )}
      </div>
    </div>
  );
};
